import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageLayout } from "@/components/PageLayout";
import { CalculatorSuperCalcCTA } from "@/components/CalculatorSuperCalcCTA";
import { calculatorCTAs } from "@/data/calculatorCTAs";

const SetTheoryCalculator = () => {
  const [setA, setSetA] = useState("");
  const [setB, setSetB] = useState("");
  const [operation, setOperation] = useState("union");
  const [result, setResult] = useState<string>("");

  const parseSet = (input: string): Set<string> => {
    return new Set(
      input
        .split(",")
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
    );
  };

  const calculateOperation = () => {
    const a = parseSet(setA);
    const b = parseSet(setB);
    let resultSet: Set<string> = new Set();

    switch (operation) {
      case "union":
        resultSet = new Set([...a, ...b]);
        break;
      case "intersection":
        resultSet = new Set([...a].filter((x) => b.has(x)));
        break;
      case "difference":
        resultSet = new Set([...a].filter((x) => !b.has(x)));
        break;
      case "symmetric-difference":
        const union = new Set([...a, ...b]);
        const intersection = new Set([...a].filter((x) => b.has(x)));
        resultSet = new Set([...union].filter((x) => !intersection.has(x)));
        break;
    }

    setResult(Array.from(resultSet).join(", "));
  };

  return (
    <PageLayout>
      <div className="max-w-2xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Set Theory Calculator</CardTitle>
            <CardDescription>Perform operations on mathematical sets</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="setA">Set A</Label>
              <Input
                id="setA"
                placeholder="1, 2, 3, 4"
                value={setA}
                onChange={(e) => setSetA(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="setB">Set B</Label>
              <Input
                id="setB"
                placeholder="3, 4, 5, 6"
                value={setB}
                onChange={(e) => setSetB(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="operation">Operation</Label>
              <Select value={operation} onValueChange={setOperation}>
                <SelectTrigger id="operation">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="union">Union (A ∪ B)</SelectItem>
                  <SelectItem value="intersection">Intersection (A ∩ B)</SelectItem>
                  <SelectItem value="difference">Difference (A - B)</SelectItem>
                  <SelectItem value="symmetric-difference">Symmetric Difference (A Δ B)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={calculateOperation} className="w-full">
              Calculate
            </Button>
            </CardContent>
        </Card>

        {result && (
          <>
            <Card className="p-6">
            <h3 className="text-lg font-semibold mb-2">Result</h3>
            <p className="text-2xl font-mono">{`{ ${result} }`}</p>
          </Card>

          <CalculatorSuperCalcCTA config={calculatorCTAs["set-theory"]} trigger={result} />
          </>
        )}

        <Card className="p-6 bg-muted/50">
          <h3 className="font-semibold mb-2">Operations</h3>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>• <strong>Union (A ∪ B):</strong> All elements in A or B</li>
            <li>• <strong>Intersection (A ∩ B):</strong> Elements in both A and B</li>
            <li>• <strong>Difference (A - B):</strong> Elements in A but not in B</li>
            <li>• <strong>Symmetric Difference (A Δ B):</strong> Elements in A or B but not both</li>
          </ul>
        </Card>
      </div>
    </PageLayout>
  );
};

export default SetTheoryCalculator;
